import { useState } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import { CloudUpload, Delete, Autorenew } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'

const UploadArea = ({ onFileSelect, uploadedPreview, onDelete, onReplace }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        onFileSelect && onFileSelect(file)
      }
    }
  }

  const handleClick = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        onFileSelect && onFileSelect(file)
      }
    }
    input.click()
  }

  // If image is uploaded, show preview with overlay buttons
  if (uploadedPreview) {
    return (
      <Box
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        sx={{
          position: 'relative',
          border: '2px solid',
          borderColor: 'grey.300',
          borderRadius: designSpecs.borderRadius.xl,
          overflow: 'hidden',
          boxShadow: 6,
          height: 300,
          transition: designSpecs.transitions.default,
        }}
      >
        {/* Image Preview */}
        <Box
          component="img"
          src={uploadedPreview}
          alt="Uploaded preview"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />

        {/* Hover Overlay with Buttons */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            opacity: isHovering ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: isHovering ? 'auto' : 'none',
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              onDelete && onDelete()
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              width: 56,
              height: 56,
              '&:hover': {
                bgcolor: 'white',
                transform: 'scale(1.1)',
              },
            }}
          >
            <Delete sx={{ fontSize: 28, color: 'error.main' }} />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              onReplace && onReplace()
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              width: 56,
              height: 56,
              '&:hover': {
                bgcolor: 'white',
                transform: 'scale(1.1)',
              },
            }}
          >
            <Autorenew sx={{ fontSize: 28, color: 'primary.main' }} />
          </IconButton>
        </Box>
      </Box>
    )
  }

  // Default upload box
  return (
    <Box
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        border: '2px dashed',
        borderColor: isDragging ? 'primary.main' : 'grey.300',
        borderRadius: designSpecs.borderRadius.xl,
        padding: '4rem',
        bgcolor: isDragging ? 'primary.light' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(4px)',
        cursor: 'pointer',
        transition: designSpecs.transitions.default,
        boxShadow: 6,
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'primary.light',
          boxShadow: 8,
          '& .upload-icon-wrapper': {
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box
          className="upload-icon-wrapper"
          sx={{
            width: 80,
            height: 80,
            background: designSpecs.gradients.primary,
            borderRadius: designSpecs.borderRadius.xl,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: 3,
            transition: designSpecs.transitions.default,
          }}
        >
          <CloudUpload sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: 'grey.900' }}>
          Upload Your Photo
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.500', fontSize: designSpecs.typography.sizes.lg, mb: 1.5 }}>
          Drag and drop or click to upload
        </Typography>
        <Typography variant="body2" sx={{ color: 'grey.400', fontSize: designSpecs.typography.sizes.sm }}>
          JPG or PNG • Up to 10MB
        </Typography>
      </Box>
    </Box>
  )
}

export default UploadArea
